import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera, MapPin, Clock, ShieldCheck, CheckCircle2, Plus, Search,
  RefreshCw, LogOut, Check, X, Users, LayoutDashboard,
  ClipboardList, CalendarDays, Timer, Shield, ChevronRight,
  Calendar, CheckSquare, Lock, Laptop,
  Briefcase, AlertTriangle, KeyRound, Sparkles, ExternalLink,
  Eye, EyeOff, Award, Target, Flame, ChevronDown, User,
  Download, Printer, FileSpreadsheet, SwitchCamera, FileText,
  Send, CheckCircle, Info, ArrowUpRight, Smartphone, AlertCircle, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { UserAvatar, WhatsAppBlankAvatar } from "@/components/WhatsAppBlankAvatar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "";
const API = `${BACKEND_URL}/api`;

const formatElapsed = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
};

function useLiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const timeStr = now.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata"
  });
  const dateStr = now.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata"
  });
  return { timeStr, dateStr };
}

export default function WelcomeAttendancePage() {
  const { user: authUser } = useAuth();
  const { timeStr, dateStr } = useLiveClock();

  // Core Data States
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [staffRoster, setStaffRoster] = useState([]);
  const [activeTab, setActiveTab] = useState("overview"); // "overview", "tasks", "daily_report", "downloads", "history"
  const [selectedDateFilter, setSelectedDateFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Authentication & Login Form States (for staff without active session)
  const [loginId, setLoginId] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submittingLogin, setSubmittingLogin] = useState(false);

  // Active Staff Override
  const [selectedStaffEmail, setSelectedStaffEmail] = useState(() => {
    return localStorage.getItem("jamadhan_staff_email") || "";
  });
  const [rememberMe, setRememberMe] = useState(true);
  const [forceStaffLogin, setForceStaffLogin] = useState(false);
  const [founderFilter, setFounderFilter] = useState("all"); // "all", "clocked_in", "standby"
  const [founderSearchQuery, setFounderSearchQuery] = useState("");
  const [newTaskAssigneeEmail, setNewTaskAssigneeEmail] = useState("");
  const [founderActiveTab, setFounderActiveTab] = useState("roster"); // "roster", "tasks", "eods"

  // Punch In / Out Modal State
  const [isPunchModalOpen, setIsPunchModalOpen] = useState(false);
  const [punchMode, setPunchMode] = useState("in"); // "in" or "out"
  const [capturedImage, setCapturedImage] = useState(null);
  const [locationStatus, setLocationStatus] = useState("locating"); // "locating", "locked", "denied"
  const [gpsCoords, setGpsCoords] = useState(null);
  const [submittingPunch, setSubmittingPunch] = useState(false);
  const [punchNotes, setPunchNotes] = useState("");
  const [eodPunchReport, setEodPunchReport] = useState("");
  const [facingMode, setFacingMode] = useState("user"); // "user" or "environment"
  const [cameraActive, setCameraActive] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);

  // Self-Task Creation Modal State
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDesc, setNewTaskDesc] = useState("");
  const [newTaskCategory, setNewTaskCategory] = useState("Operations");
  const [newTaskPriority, setNewTaskPriority] = useState("P0 (Urgent)");
  const [newTaskTimeline, setNewTaskTimeline] = useState(new Date().toISOString().split("T")[0]);
  const [newTaskChecklist, setNewTaskChecklist] = useState("");
  const [submittingTask, setSubmittingTask] = useState(false);
  const [taskFilter, setTaskFilter] = useState("all"); // "all", "in_progress", "completed", "admin", "self"

  // Photo & Record Inspector Modal
  const [inspectedLog, setInspectedLog] = useState(null);

  // Profile Picture Self-Service Modal
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Daily Work Report States
  const [dailyReportsList, setDailyReportsList] = useState([]);
  const [dailyReportSummary, setDailyReportSummary] = useState("");
  const [dailyReportBlockers, setDailyReportBlockers] = useState("");
  const [submittingDailyReport, setSubmittingDailyReport] = useState(false);
  const [loadingDailyReports, setLoadingDailyReports] = useState(false);

  // Report Download / Export States
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadReportData, setDownloadReportData] = useState(null);
  const [loadingDownload, setLoadingDownload] = useState(false);

  // Fetch Workstation State
  const fetchData = async (overrideEmail) => {
    try {
      setLoading(true);
      const emailToUse = overrideEmail !== undefined ? overrideEmail : (selectedStaffEmail || localStorage.getItem("jamadhan_staff_email") || "");
      const headers = emailToUse ? { "X-Team-Email": emailToUse } : {};

      const [res, rosterRes] = await Promise.all([
        axios.get(`${API}/attendance/me`, { headers, withCredentials: true }),
        axios.get(`${API}/attendance/staff-roster`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);

      setData(res.data);
      setStaffRoster(rosterRes.data || []);

      if (res.data?.today?.elapsed_seconds) {
        setElapsedSeconds(res.data.today.elapsed_seconds);
      }
      if (res.data?.today?.eod_report && !dailyReportSummary) {
        setDailyReportSummary(res.data.today.eod_report);
      }
    } catch (err) {
      console.error("Failed to load workstation data:", err);
      toast.error("Could not load workstation data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyReports = async () => {
    try {
      setLoadingDailyReports(true);
      const emailToUse = selectedStaffEmail || localStorage.getItem("jamadhan_staff_email");
      const headers = emailToUse ? { "X-Team-Email": emailToUse } : {};
      const res = await axios.get(`${API}/attendance/daily-reports`, { headers, withCredentials: true });
      if (res.data?.reports) {
        setDailyReportsList(res.data.reports);
      }
    } catch (err) {
      console.error("Error loading daily reports:", err);
    } finally {
      setLoadingDailyReports(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDailyReports();
    const savedLoginId = localStorage.getItem("jamadhan_saved_login_id");
    if (savedLoginId) {
      setLoginId(savedLoginId);
    }
  }, [selectedStaffEmail]);

  // Live Timer increment when clocked in
  useEffect(() => {
    if (!data?.today?.is_clocked_in) return;
    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [data?.today?.is_clocked_in]);

  // Staff Login Submit
  const handleStaffLogin = async (e) => {
    e.preventDefault();
    if (!loginId.trim() || !loginPassword.trim()) {
      toast.error("Please enter both User ID/Email and Password");
      return;
    }
    setSubmittingLogin(true);
    try {
      const res = await axios.post(`${API}/attendance/login`, {
        login_id: loginId.trim(),
        password: loginPassword.trim()
      }, { withCredentials: true });

      if (res.data?.success) {
        toast.success(`Welcome, ${res.data.user?.name}! Workstation unlocked.`);
        const staffEmail = res.data.user?.email;
        if (staffEmail) {
          localStorage.setItem("jamadhan_staff_email", staffEmail);
          setSelectedStaffEmail(staffEmail);
        }
        if (rememberMe) {
          localStorage.setItem("jamadhan_staff_remember", "true");
          localStorage.setItem("jamadhan_saved_login_id", loginId.trim());
          if (res.data.session_token) {
            localStorage.setItem("jamadhan_staff_token", res.data.session_token);
          }
        } else {
          localStorage.removeItem("jamadhan_staff_remember");
          localStorage.removeItem("jamadhan_saved_login_id");
          localStorage.removeItem("jamadhan_staff_token");
        }
        setForceStaffLogin(false);
        fetchData(staffEmail);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials. Please contact your manager.");
    } finally {
      setSubmittingLogin(false);
    }
  };

  // Pre-fill user ID from quick roster picker
  const handleQuickSelectMember = (member) => {
    setLoginId(member.email || member.username);
    const pwdInput = document.getElementById("staff_pwd_input");
    if (pwdInput) pwdInput.focus();
    toast.info(`Selected ${member.name}. Enter your assigned password.`);
  };

  // Exit / Log Out of Workstation
  const handleSignOutWorkstation = async () => {
    localStorage.removeItem("jamadhan_staff_email");
    localStorage.removeItem("jamadhan_staff_remember");
    localStorage.removeItem("jamadhan_staff_token");
    setSelectedStaffEmail("");
    setForceStaffLogin(true);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    } catch {}
    setData(null);
    toast.info("Signed out from workstation console");
    fetchData("");
  };

  // Camera Management with Switch Camera (front/back) support
  const startCamera = async (overrideFacing) => {
    const mode = overrideFacing || facingMode;
    setCameraActive(true);
    try {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(t => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 640 },
          height: { ideal: 640 }
        },
        audio: false
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      setCameraActive(false);
      toast.warning("Camera permission is required for selfie verification");
    }
  };

  const stopCamera = () => {
    mediaStreamRef.current?.getTracks().forEach(t => t.stop());
    mediaStreamRef.current = null;
    setCameraActive(false);
  };

  const toggleCameraFacingMode = () => {
    const nextMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(nextMode);
    startCamera(nextMode);
    toast.info(`Switched to ${nextMode === "user" ? "Front" : "Back"} camera`);
  };

  const acquireLocation = () => {
    setLocationStatus("locating");
    if (!navigator.geolocation) {
      setLocationStatus("denied");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setGpsCoords({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        });
        setLocationStatus("locked");
      },
      () => {
        // Jaipur Flagship Academy Fallback
        setGpsCoords({ latitude: 26.8928, longitude: 75.7612, accuracy: 15 });
        setLocationStatus("locked");
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };

  const openPunchModal = (mode) => {
    setPunchMode(mode);
    setCapturedImage(null);
    setPunchNotes("");
    setEodPunchReport(data?.today?.eod_report || dailyReportSummary || "");
    setIsPunchModalOpen(true);
    startCamera("user");
    acquireLocation();
  };

  const captureSelfie = () => {
    if (videoRef.current && canvasRef.current) {
      const v = videoRef.current;
      const c = canvasRef.current;
      c.width = v.videoWidth || 480;
      c.height = v.videoHeight || 480;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      setCapturedImage(c.toDataURL("image/jpeg", 0.88));
      stopCamera();
    }
  };

  const handleSubmitPunch = async () => {
    if (!capturedImage) {
      toast.error("Please capture your verification selfie first");
      return;
    }
    setSubmittingPunch(true);
    try {
      const ep = punchMode === "in" ? `${API}/attendance/punch-in` : `${API}/attendance/punch-out`;
      const emailToUse = selectedStaffEmail || localStorage.getItem("jamadhan_staff_email");
      const headers = emailToUse ? { "X-Team-Email": emailToUse } : {};

      const payload = {
        selfie_image: capturedImage,
        latitude: gpsCoords?.latitude,
        longitude: gpsCoords?.longitude,
        accuracy: gpsCoords?.accuracy,
        notes: punchNotes,
        work_mode: "onsite"
      };

      if (punchMode === "out") {
        payload.eod_report = eodPunchReport || punchNotes;
      }

      const res = await axios.post(ep, payload, { headers, withCredentials: true });

      toast.success(res.data?.message || (punchMode === "in" ? "Clocked in successfully" : "Clocked out successfully"));
      setIsPunchModalOpen(false);
      stopCamera();
      fetchData();
      if (punchMode === "out") {
        fetchDailyReports();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Attendance record failed. Please retry.");
    } finally {
      setSubmittingPunch(false);
    }
  };

  // Self-Tasking: Member assigns task to themselves
  const handleCreateSelfTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) {
      toast.error("Please provide a task title");
      return;
    }

    setSubmittingTask(true);
    try {
      const currentEmail = data?.user?.email || selectedStaffEmail;
      const currentName = data?.user?.name || "Self";
      let assignedEmail = currentEmail;
      let assignedName = currentName;
      if (newTaskAssigneeEmail) {
        const found = staffRoster.find(m => m.email === newTaskAssigneeEmail) ||
          data?.founder_overview?.members?.find(m => m.email === newTaskAssigneeEmail);
        if (found) {
          assignedEmail = found.email;
          assignedName = found.name;
        }
      }

      const checklistItems = newTaskChecklist
        .split("\n")
        .map(t => t.trim())
        .filter(Boolean)
        .map((text, idx) => ({ id: `c_${Date.now()}_${idx}`, text, done: false }));

      await axios.post(`${API}/attendance/tasks`, {
        title: newTaskTitle.trim(),
        description: newTaskDesc.trim(),
        category: newTaskCategory,
        priority: newTaskPriority,
        timeline_date: newTaskTimeline || undefined,
        assigned_to_email: assignedEmail,
        assigned_to_name: assignedName,
        checklist: checklistItems
      }, { withCredentials: true });

      toast.success(`Deliverable assigned to ${assignedName}`);
      setIsTaskModalOpen(false);
      setNewTaskTitle("");
      setNewTaskDesc("");
      setNewTaskChecklist("");
      setNewTaskAssigneeEmail("");
      fetchData();
    } catch (err) {
      toast.error("Failed to create task");
    } finally {
      setSubmittingTask(false);
    }
  };

  // Toggle Task Status (To Do -> In Progress -> Completed)
  const handleToggleTaskStatus = async (taskId, currentStatus) => {
    let nextStatus = "in_progress";
    if (currentStatus === "in_progress") nextStatus = "completed";
    else if (currentStatus === "completed") nextStatus = "todo";

    try {
      await axios.patch(`${API}/attendance/tasks/${taskId}`, { status: nextStatus }, { withCredentials: true });
      toast.success(nextStatus === "completed" ? "Task marked as completed" : `Task status updated: ${nextStatus}`);
      fetchData();
    } catch {
      toast.error("Failed to update task status");
    }
  };

  // Toggle Interactive Checklist Item
  const handleToggleChecklistItem = async (task, itemIndex) => {
    if (!task.checklist) return;
    const updatedChecklist = task.checklist.map((item, idx) =>
      idx === itemIndex ? { ...item, done: !item.done } : item
    );
    try {
      await axios.patch(`${API}/attendance/tasks/${task.task_id}`, { checklist: updatedChecklist }, { withCredentials: true });
      fetchData();
    } catch {
      toast.error("Failed to update checklist item");
    }
  };

  // Submit Daily Work Report
  const handleSubmitDailyReport = async (e) => {
    e.preventDefault();
    if (!dailyReportSummary.trim()) {
      toast.error("Please enter a summary of your day's work");
      return;
    }
    setSubmittingDailyReport(true);
    try {
      const emailToUse = selectedStaffEmail || localStorage.getItem("jamadhan_staff_email");
      const headers = emailToUse ? { "X-Team-Email": emailToUse } : {};

      await axios.post(`${API}/attendance/daily-report`, {
        summary: dailyReportSummary.trim(),
        blockers: dailyReportBlockers.trim(),
        date: new Date().toISOString().split("T")[0]
      }, { headers, withCredentials: true });

      toast.success("Daily Work Report submitted successfully");
      setDailyReportSummary("");
      setDailyReportBlockers("");
      fetchDailyReports();
      fetchData();
    } catch (err) {
      toast.error("Failed to submit daily report");
    } finally {
      setSubmittingDailyReport(false);
    }
  };

  // Suggest today's completed tasks without overwriting existing content
  const handleSuggestCompletedTasks = () => {
    const completedTasks = (data?.tasks || []).filter(t => t.status === "completed");
    if (completedTasks.length === 0) {
      toast.info("No completed tasks found today. Enter your accomplishments manually.");
      return;
    }
    const bullets = completedTasks.map((t, idx) => `• ${t.title} (${t.category || "Operations"})`).join("\n");
    const suggestion = `Completed Tasks Today:\n${bullets}`;
    
    setDailyReportSummary(prev => {
      if (!prev.trim()) return suggestion;
      return `${prev.trim()}\n\n${suggestion}`;
    });
    toast.success("Added completed tasks to your report draft");
  };

  const handleSuggestCompletedTasksForPunch = () => {
    const completedTasks = (data?.tasks || []).filter(t => t.status === "completed");
    if (completedTasks.length === 0) {
      toast.info("No completed tasks found in your task list. Type your accomplishments manually.");
      return;
    }
    const bullets = completedTasks.map((t) => `• ${t.title} (${t.category || "General"})`).join("\n");
    const suggestion = `Completed Tasks Today:\n${bullets}`;
    setEodPunchReport(prev => prev.trim() ? `${prev.trim()}\n\n${suggestion}` : suggestion);
    toast.success("Auto-filled completed tasks!");
  };

  // Handle Download / Preview Report
  const handleOpenReportDownload = async (days) => {
    setLoadingDownload(true);
    try {
      const emailToUse = selectedStaffEmail || localStorage.getItem("jamadhan_staff_email");
      const headers = emailToUse ? { "X-Team-Email": emailToUse } : {};
      const res = await axios.get(`${API}/attendance/reports/download?days=${days}`, { headers, withCredentials: true });
      if (res.data?.success) {
        setDownloadReportData(res.data);
        setDownloadModalOpen(true);
      }
    } catch (err) {
      toast.error("Failed to generate report");
    } finally {
      setLoadingDownload(false);
    }
  };

  // Export report data to CSV
  const exportReportToCSV = () => {
    if (!downloadReportData || !downloadReportData.records) return;
    const headers = ["Date", "Day", "Punch In Time", "Punch Out Time", "Duration", "Status", "Verified Location", "Daily EOD Work Report"];
    const rows = downloadReportData.records.map(r => [
      r.date,
      r.day,
      r.punch_in_time,
      r.punch_out_time,
      r.duration,
      r.status,
      `"${(r.location || '').replace(/"/g, '""')}"`,
      `"${(r.eod_report || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `jamadhan_report_${downloadReportData.member?.name || 'staff'}_${downloadReportData.period_days}days.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV report downloaded");
  };

  // Self-Service Profile Picture Upload
  const handleUploadMyPhoto = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image file must be under 6MB");
      return;
    }
    setUploadingPhoto(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64Data = e.target.result;
      try {
        const emailToUse = currentUser?.email || selectedStaffEmail || localStorage.getItem("jamadhan_staff_email");
        await axios.post(`${API}/attendance/profile/picture`, {
          email: emailToUse,
          picture: base64Data
        }, { withCredentials: true });

        toast.success("Profile picture updated!");
        setData(prev => prev ? {
          ...prev,
          user: { ...prev.user, picture: base64Data }
        } : prev);
        setShowPhotoModal(false);
      } catch (err) {
        toast.error("Failed to update profile picture");
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Remove photo and revert back to clean WhatsApp blank DP
  const handleRemoveMyPhoto = async () => {
    try {
      const emailToUse = currentUser?.email || selectedStaffEmail || localStorage.getItem("jamadhan_staff_email");
      await axios.post(`${API}/attendance/profile/picture`, {
        email: emailToUse,
        picture: ""
      }, { withCredentials: true });

      toast.success("Profile picture removed (reverted to blank DP)");
      setData(prev => prev ? {
        ...prev,
        user: { ...prev.user, picture: "" }
      } : prev);
      setShowPhotoModal(false);
    } catch {
      toast.error("Failed to remove profile picture");
    }
  };

  const isClockedIn = Boolean(data?.today?.is_clocked_in);
  const currentUser = data?.user || {};
  const todayRecord = data?.today || {};
  const tasks = data?.tasks || [];
  const history40Days = data?.history_40_days || [];

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let list = [...tasks];
    if (taskFilter === "admin") list = list.filter(t => t.is_admin_assigned || t.created_by_email?.includes("deepak"));
    else if (taskFilter === "self") list = list.filter(t => !t.is_admin_assigned && !t.created_by_email?.includes("deepak"));
    else if (taskFilter === "in_progress") list = list.filter(t => t.status === "in_progress" || t.status === "todo");
    else if (taskFilter === "completed") list = list.filter(t => t.status === "completed");
    return list;
  }, [tasks, taskFilter]);

  // Filter 40-day records
  const filteredRecords = useMemo(() => {
    let list = [...history40Days];
    if (selectedDateFilter === "present") list = list.filter(r => r.status === "present" || r.status === "clocked_in");
    else if (selectedDateFilter === "off") list = list.filter(r => r.status === "weekend_off");
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(r => r.display_date?.toLowerCase().includes(q) || r.day_name?.toLowerCase().includes(q));
    }
    return list;
  }, [history40Days, selectedDateFilter, searchQuery]);

  // Derived shift progress metrics (e.g. 8.5 hours standard shift target)
  const shiftTargetSecs = 8.5 * 3600;
  const shiftProgressPercent = Math.min(100, Math.round((elapsedSeconds / shiftTargetSecs) * 100));
  const todayDoneTasks = tasks.filter(t => t.status === "completed").length;

  // =========================================================================
  // VIEW 1: LOADING STATE
  // =========================================================================
  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-slate-800">
        <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200/80 shadow-sm flex items-center justify-center mb-4">
          <RefreshCw className="w-5 h-5 text-emerald-600 animate-spin" />
        </div>
        <h3 className="text-base font-semibold text-slate-900 tracking-tight">Loading Workstation</h3>
        <p className="text-xs text-slate-500 mt-1">Connecting to authoritative server & location services...</p>
      </div>
    );
  }

  // =========================================================================
  // VIEW 2: FOUNDER & ADMIN EXECUTIVE OVERSIGHT (DEEPAK SIR)
  // =========================================================================
  if (data?.is_founder && !selectedStaffEmail && !forceStaffLogin) {
    const founderOverview = data?.founder_overview || {};
    const allMembers = founderOverview.members || staffRoster || [];
    const recentTasks = founderOverview.recent_tasks || [];
    const recentEods = founderOverview.recent_eods || [];

    const filteredMembers = allMembers.filter((m) => {
      if (founderFilter === "clocked_in" && !m.is_clocked_in) return false;
      if (founderFilter === "standby" && m.is_clocked_in) return false;
      if (founderSearchQuery.trim()) {
        const q = founderSearchQuery.toLowerCase();
        return (
          m.name?.toLowerCase().includes(q) ||
          m.email?.toLowerCase().includes(q) ||
          m.department?.toLowerCase().includes(q) ||
          m.team_title?.toLowerCase().includes(q)
        );
      }
      return true;
    });

    return (
      <div className="min-h-screen bg-slate-50 text-slate-800 font-sans pb-24 selection:bg-amber-100 selection:text-amber-900">
        {/* Executive Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center text-amber-400 font-bold text-xs tracking-tight shadow-sm shrink-0">
                JD
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900 text-sm tracking-tight">Jamadhan OS</span>
                  <span className="text-slate-300">/</span>
                  <span className="text-xs text-slate-600 font-medium">Executive Oversight</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                    <Sparkles className="w-3 h-3 text-amber-600" />
                    Founder Mode • Attendance Exempt
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right hidden sm:block">
                <p className="text-xs font-semibold text-slate-900 font-mono tracking-tight">{timeStr}</p>
                <p className="text-[11px] text-slate-500">{dateStr}</p>
              </div>

              <div className="flex items-center gap-2.5 pl-3 sm:border-l sm:border-slate-200">
                <UserAvatar
                  src={currentUser.picture}
                  alt={currentUser.name}
                  className="w-8 h-8 rounded-full ring-2 ring-amber-400"
                />
                <div className="hidden md:block text-left min-w-0">
                  <p className="text-xs font-semibold text-slate-900 truncate">{currentUser.name}</p>
                  <p className="text-[10px] text-amber-700 font-semibold uppercase tracking-wider">Founder &amp; MD</p>
                </div>

                <button
                  onClick={() => {
                    fetchData();
                    toast.success("Live campus floor updated");
                  }}
                  title="Refresh live status"
                  className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={() => setForceStaffLogin(true)}
                  title="Switch to employee terminal login"
                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border border-slate-200 transition-colors flex items-center gap-1.5"
                >
                  <Users className="w-3.5 h-3.5 text-slate-600" />
                  <span className="hidden sm:inline">Staff Terminal</span>
                </button>

                <a
                  href="/admin/team"
                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-white bg-slate-900 hover:bg-slate-800 transition-colors flex items-center gap-1.5 shadow-sm"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Team Operations</span>
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Executive Banner */}
        <div className="bg-gradient-to-r from-amber-500/10 via-emerald-500/10 to-transparent border-b border-amber-200/60 px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-amber-500 text-slate-950 flex items-center justify-center font-bold text-xs shadow-sm">
                👑
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-900">
                  Founder Command Center · Deepak Sethia
                </p>
                <p className="text-[11px] text-slate-600">
                  Attendance punch is exempt for Founder &amp; Managing Director. Live team presence, geofence validations, and deliverables are tracked below.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setNewTaskAssigneeEmail("");
                  setIsTaskModalOpen(true);
                }}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs shadow-sm flex items-center gap-1.5 transition-all"
              >
                <Plus className="w-3.5 h-3.5" />
                Assign Deliverable
              </button>
            </div>
          </div>
        </div>

        {/* Executive Body */}
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* 4 KPI Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Registered Staff</span>
                <Users className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold font-mono text-slate-900 mt-2">
                {founderOverview.total_members || allMembers.length}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">Active workforce roster</p>
            </div>

            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-emerald-200/80 shadow-sm bg-gradient-to-br from-white to-emerald-50/30">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-emerald-800">Clocked In Now</span>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-600"></span>
                </span>
              </div>
              <p className="text-2xl font-bold font-mono text-emerald-700 mt-2">
                {founderOverview.clocked_in_now || 0}
              </p>
              <p className="text-[11px] text-emerald-600 mt-0.5">Active shifts on campus</p>
            </div>

            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">EOD Reports Today</span>
                <FileText className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-2xl font-bold font-mono text-blue-700 mt-2">
                {founderOverview.eod_reports_count || 0}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">Daily work summaries submitted</p>
            </div>

            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Team Deliverables</span>
                <CheckSquare className="w-4 h-4 text-purple-500" />
              </div>
              <p className="text-2xl font-bold font-mono text-purple-700 mt-2">
                {founderOverview.tasks_count || 0}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">Active operational tasks</p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center justify-between border-b border-slate-200 gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setFounderActiveTab("roster")}
                className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all flex items-center gap-2 ${
                  founderActiveTab === "roster"
                    ? "border-emerald-600 text-emerald-700 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                Live Campus Roster ({allMembers.length})
              </button>
              <button
                onClick={() => setFounderActiveTab("tasks")}
                className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all flex items-center gap-2 ${
                  founderActiveTab === "tasks"
                    ? "border-emerald-600 text-emerald-700 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                <CheckSquare className="w-3.5 h-3.5" />
                Team Tasks ({recentTasks.length})
              </button>
              <button
                onClick={() => setFounderActiveTab("eods")}
                className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all flex items-center gap-2 ${
                  founderActiveTab === "eods"
                    ? "border-emerald-600 text-emerald-700 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                Daily EOD Reports ({recentEods.length})
              </button>
            </div>

            {founderActiveTab === "roster" && (
              <div className="flex items-center gap-2 pb-2 sm:pb-0">
                <div className="flex items-center bg-white border border-slate-200 rounded-lg p-0.5 text-xs">
                  <button
                    onClick={() => setFounderFilter("all")}
                    className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                      founderFilter === "all" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    All ({allMembers.length})
                  </button>
                  <button
                    onClick={() => setFounderFilter("clocked_in")}
                    className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                      founderFilter === "clocked_in" ? "bg-emerald-600 text-white" : "text-slate-600 hover:text-emerald-700"
                    }`}
                  >
                    Clocked In ({founderOverview.clocked_in_now || 0})
                  </button>
                  <button
                    onClick={() => setFounderFilter("standby")}
                    className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                      founderFilter === "standby" ? "bg-slate-200 text-slate-900" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    Standby ({Math.max(0, (allMembers.length || 0) - (founderOverview.clocked_in_now || 0))})
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* TAB 1: LIVE CAMPUS ROSTER */}
          {founderActiveTab === "roster" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMembers.map((m) => (
                  <div
                    key={m.email || m.user_id}
                    className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="relative shrink-0">
                          <UserAvatar
                            src={m.picture}
                            alt={m.name}
                            className="w-11 h-11 rounded-xl ring-1 ring-slate-200"
                          />
                          <span
                            className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${
                              m.is_clocked_in ? "bg-emerald-500 animate-pulse" : "bg-slate-300"
                            }`}
                          />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-sm font-semibold text-slate-900 truncate">{m.name}</h4>
                          <p className="text-xs text-slate-500 truncate">{m.team_title || m.department || "Team Member"}</p>
                          <p className="text-[10px] text-slate-400 font-mono truncate">{m.email}</p>
                        </div>
                      </div>

                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium shrink-0 ${
                          m.is_clocked_in
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : m.status === "clocked_out"
                            ? "bg-blue-50 text-blue-700 border border-blue-200"
                            : "bg-slate-100 text-slate-600 border border-slate-200"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            m.is_clocked_in ? "bg-emerald-600 animate-pulse" : "bg-slate-400"
                          }`}
                        />
                        {m.is_clocked_in
                          ? "Clocked In"
                          : m.status === "clocked_out"
                          ? "Shift Completed"
                          : "Standby"}
                      </span>
                    </div>

                    {/* Punch details */}
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs space-y-1.5">
                      {m.is_clocked_in ? (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">In Time:</span>
                            <span className="font-mono font-semibold text-emerald-700">{m.punch_in_time}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Logged Duration:</span>
                            <span className="font-mono font-medium text-slate-900">
                              {formatElapsed(m.elapsed_seconds || 0)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 pt-1 border-t border-slate-200/60">
                            <MapPin className="w-3 h-3 text-emerald-600 shrink-0" />
                            <span className="truncate">{m.location?.split("(")[0]?.trim() || "Jaipur Campus Verified"}</span>
                          </div>
                          {m.selfie_preview && (
                            <button
                              onClick={() => setInspectedLog({ ...m, punch_in_selfie: m.selfie_preview, display_date: "Today" })}
                              className="text-[11px] text-emerald-700 font-medium hover:underline flex items-center gap-1 pt-0.5"
                            >
                              <Camera className="w-3 h-3" />
                              View Check-In Selfie
                            </button>
                          )}
                        </>
                      ) : m.status === "clocked_out" ? (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Shift Times:</span>
                            <span className="font-mono text-slate-800">{m.punch_in_time} → {m.punch_out_time}</span>
                          </div>
                          <div className="flex items-center justify-between text-[11px] text-slate-500">
                            <span>Status:</span>
                            <span className="text-blue-700 font-medium">EOD Report Submitted</span>
                          </div>
                        </>
                      ) : (
                        <div className="py-2 text-center text-slate-400 text-xs">
                          Pending on-site punch-in today
                        </div>
                      )}
                    </div>

                    {/* Card Actions */}
                    <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-100">
                      <button
                        onClick={() => {
                          setSelectedStaffEmail(m.email);
                          fetchData(m.email);
                          toast.info(`Inspecting workstation for ${m.name}`);
                        }}
                        className="py-1.5 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-xs transition-colors flex items-center justify-center gap-1.5"
                      >
                        <Laptop className="w-3.5 h-3.5 text-slate-600" />
                        Inspect Station
                      </button>
                      <button
                        onClick={() => {
                          setNewTaskAssigneeEmail(m.email);
                          setIsTaskModalOpen(true);
                        }}
                        className="py-1.5 px-3 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-medium text-xs transition-colors flex items-center justify-center gap-1.5 border border-emerald-200/60"
                      >
                        <Plus className="w-3.5 h-3.5 text-emerald-600" />
                        Assign Task
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 2: TEAM TASKS */}
          {founderActiveTab === "tasks" && (
            <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Today's Team Operational Deliverables</h3>
                  <p className="text-xs text-slate-500">Tasks assigned across the team with real-time completion state</p>
                </div>
                <button
                  onClick={() => {
                    setNewTaskAssigneeEmail("");
                    setIsTaskModalOpen(true);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs shadow-sm flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  + Assign New Task
                </button>
              </div>

              {recentTasks.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  No deliverables assigned yet today. Click "+ Assign New Task" to create one.
                </div>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                  {recentTasks.map((t) => (
                    <div key={t.task_id} className="p-4 hover:bg-slate-50 transition-colors flex items-start justify-between gap-4">
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                            t.priority?.includes("Urgent") || t.priority === "high"
                              ? "bg-rose-50 text-rose-700 border border-rose-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}>
                            {t.priority}
                          </span>
                          <span className="text-[11px] font-medium text-slate-500 px-2 py-0.5 rounded bg-slate-100">
                            {t.category || "Operations"}
                          </span>
                          <span className="text-xs font-semibold text-slate-900">
                            {t.title}
                          </span>
                        </div>
                        {t.description && (
                          <p className="text-xs text-slate-500 line-clamp-1">{t.description}</p>
                        )}
                        <p className="text-[11px] text-slate-400">
                          Assigned to: <strong className="text-slate-700">{t.assigned_to_name || t.assigned_to_email}</strong>
                        </p>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => handleToggleTaskStatus(t.task_id, t.status)}
                          className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                            t.status === "completed"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                          }`}
                        >
                          {t.status === "completed" ? "✓ Done" : "Mark Done"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: SUBMITTED EOD REPORTS */}
          {founderActiveTab === "eods" && (
            <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Daily End-of-Day Work Reports</h3>
                <p className="text-xs text-slate-500">Summary of deliverables and obstacles reported by team members today</p>
              </div>

              {recentEods.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  No EOD reports filed yet today. Reports appear as team members punch out.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recentEods.map((r, idx) => (
                    <div key={r.report_id || idx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-xs text-slate-900">{r.user_name || r.user_email}</span>
                        <span className="text-[10px] font-mono text-slate-400">{r.submitted_at?.split("T")[1]?.slice(0, 5) || "Today"}</span>
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed">{r.summary}</p>
                      {r.blockers && (
                        <div className="p-2 rounded-lg bg-amber-50 border border-amber-200/60 text-[11px] text-amber-800">
                          <strong>Blockers:</strong> {r.blockers}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    );
  }

  // =========================================================================
  // VIEW 3: UNAUTHENTICATED / STAFF LOGIN REQUIRED
  // =========================================================================
  if (!data?.is_team_member || forceStaffLogin) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-between p-4 sm:p-8 font-sans">
        {/* Top Header */}
        <header className="max-w-5xl mx-auto w-full pt-2 pb-6 flex items-center justify-between border-b border-slate-200/80">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center text-white font-bold text-sm tracking-tight shadow-sm">
              JD
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900 text-base tracking-tight">Jamadhan OS</span>
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                  Workforce Console
                </span>
              </div>
              <p className="text-xs text-slate-500">Jaipur Flagship Academy</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900 font-mono">{timeStr}</p>
              <p className="text-xs text-slate-500">{dateStr}</p>
            </div>
            {data?.is_founder && (
              <button
                onClick={() => setForceStaffLogin(false)}
                className="px-3 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold text-xs transition-colors flex items-center gap-1"
              >
                ← Back to Founder Console
              </button>
            )}
          </div>
        </header>

        {/* Main Sign-In Section */}
        <main className="max-w-5xl mx-auto w-full my-auto py-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 space-y-5">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200/60 text-emerald-700 text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              On-Site Attendance &amp; Performance Management
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
              Sign in to your <br />
              <span className="text-emerald-700">personal work console</span>
            </h1>

            <p className="text-sm text-slate-600 leading-relaxed max-w-md">
              Log in from your phone or laptop. Clock in with live photo verification and campus GPS, manage daily operational deliverables, and file your End-of-Day report.
            </p>

            <div className="p-4 rounded-xl bg-white border border-slate-200/80 shadow-sm space-y-1.5">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-800">
                <Lock className="w-3.5 h-3.5 text-slate-600" />
                <span>Auto-Save Enabled</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Check <strong>Remember My Workstation</strong> to remain permanently logged in on your phone or computer. Tomorrow morning when you open <code>jamadhan.in/welcome</code>, you will directly see your dashboard!
              </p>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 tracking-tight">Staff Sign In</h3>
                <p className="text-xs text-slate-500 mt-0.5">Enter your credentials to unlock your workstation.</p>
              </div>

              <form onSubmit={handleStaffLogin} className="space-y-4" autoComplete="on">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    User ID or Email Address *
                  </label>
                  <input
                    id="staff_login_id"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    placeholder="e.g. rahul or rahul@jamadhan.in"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      id="staff_pwd_input"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      placeholder="••••••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all pr-10 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Remember Me Checkbox */}
                <div className="flex items-start gap-2.5 pt-1">
                  <input
                    type="checkbox"
                    id="remember_me_checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                  <label htmlFor="remember_me_checkbox" className="text-xs text-slate-700 cursor-pointer select-none leading-snug">
                    <strong className="text-slate-900 font-semibold">Remember My Workstation (Auto-Login)</strong>
                    <span className="block text-[11px] text-slate-500">
                      Save credentials on this device so next time you open jamadhan.in/welcome, you directly see your dashboard without signing in again.
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submittingLogin}
                  className="w-full py-2.5 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm shadow-sm transition-all flex items-center justify-center gap-2"
                >
                  {submittingLogin ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4" />
                  )}
                  {submittingLogin ? "Verifying..." : "Sign In & Unlock Workstation"}
                </button>
              </form>

              {staffRoster.length > 0 && (
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-slate-400" />
                    Quick Member Selection
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {staffRoster.slice(0, 6).map((m) => (
                      <button
                        key={m.email}
                        type="button"
                        onClick={() => handleQuickSelectMember(m)}
                        className="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200/60 transition-all text-left flex items-center gap-2 group"
                      >
                        <UserAvatar
                          src={m.picture}
                          alt={m.name}
                          className="w-6 h-6 rounded-md"
                        />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-800 truncate">{m.name}</p>
                          <p className="text-[10px] text-slate-500 truncate font-normal">{m.team_title || m.role || "Team Member"}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <footer className="max-w-5xl mx-auto w-full pt-4 text-center text-xs text-slate-400 border-t border-slate-200/80">
          Jamadhan Educational Institute · Jaipur Flagship Academy · Authoritative IST Time System
        </footer>
      </div>
    );
  }

  // =========================================================================
  // VIEW 3: AUTHENTICATED EMPLOYEE CONSOLE (PREMIUM LIGHT ENTERPRISE)
  // =========================================================================
  return (
    <div className="min-h-screen bg-slate-50/80 text-slate-800 font-sans pb-24 selection:bg-emerald-100 selection:text-emerald-900">
      
      {/* ===================== TOP APPLICATION APP BAR ===================== */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          
          {/* Brand & Workspace Name */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center text-white font-bold text-xs tracking-tight shadow-sm shrink-0">
              JD
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900 text-sm tracking-tight">Jamadhan OS</span>
                <span className="text-slate-300">/</span>
                <span className="text-xs text-slate-500 font-medium">Workstation</span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  isClockedIn ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-600 border border-slate-200"
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isClockedIn ? "bg-emerald-600 animate-pulse" : "bg-slate-400"}`} />
                  {isClockedIn ? "Shift Active" : "Standby"}
                </span>
              </div>
            </div>
          </div>

          {/* Right Clock & User Profile */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Live Clock */}
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-slate-900 font-mono tracking-tight">{timeStr}</p>
              <p className="text-[11px] text-slate-500">{dateStr}</p>
            </div>

            {/* Profile Pill */}
            <div className="flex items-center gap-2.5 pl-3 sm:border-l sm:border-slate-200">
              <button
                type="button"
                onClick={() => setShowPhotoModal(true)}
                title="Click to view or change your profile picture"
                className="relative group shrink-0"
              >
                <UserAvatar
                  src={currentUser.picture}
                  alt={currentUser.name}
                  className="w-9 h-9 rounded-full ring-2 ring-emerald-500/30"
                />
                <span className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-3.5 h-3.5 text-white" />
                </span>
              </button>
              <div className="hidden md:block text-left min-w-0">
                <p className="text-xs font-semibold text-slate-900 truncate">{currentUser.name}</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-[11px] text-slate-500 truncate">{currentUser.team_title || currentUser.department || "Staff"}</p>
                  <button
                    type="button"
                    onClick={() => setShowPhotoModal(true)}
                    className="text-[10px] text-emerald-600 hover:text-emerald-700 font-semibold hover:underline"
                  >
                    Change DP
                  </button>
                </div>
              </div>

              {/* Refresh button */}
              <button
                onClick={() => {
                  fetchData();
                  fetchDailyReports();
                  toast.success("Workstation updated");
                }}
                title="Refresh workstation"
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>

              {/* Sign out button */}
              <button
                onClick={handleSignOutWorkstation}
                title="Sign out of console"
                className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors flex items-center gap-1.5"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Exit</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* FOUNDER INSPECTION BANNER (If Founder inspecting staff) */}
      {data?.is_founder && selectedStaffEmail && (
        <div className="bg-amber-500 text-slate-950 px-4 py-2.5 text-xs font-medium border-b border-amber-600 shadow-sm">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-slate-950 shrink-0" />
              <span>
                <strong>Executive Inspection Mode:</strong> Viewing personal workstation for <strong>{currentUser.name}</strong> ({selectedStaffEmail}).
              </span>
            </div>
            <button
              onClick={() => {
                setSelectedStaffEmail("");
                localStorage.removeItem("jamadhan_staff_email");
                fetchData("");
                toast.info("Returned to Founder Command Center");
              }}
              className="px-3 py-1 rounded-lg bg-slate-950 hover:bg-slate-800 text-white font-semibold text-xs transition-colors flex items-center gap-1.5 shadow-sm"
            >
              <span>← Back to Founder Command Center</span>
            </button>
          </div>
        </div>
      )}

      {/* ===================== MAIN CONTENT CONTAINER ===================== */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 space-y-6">

        {/* =========================================================================
            SECTION 1: ATTENDANCE & SHIFT OVERVIEW CARD
            ========================================================================= */}
        <div className="p-5 sm:p-6 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
            
            {/* Shift Metrics */}
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  isClockedIn
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : todayRecord.punch_out_time
                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                    : "bg-slate-100 text-slate-700 border border-slate-200"
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isClockedIn ? "bg-emerald-600 animate-pulse" : todayRecord.punch_out_time ? "bg-blue-600" : "bg-slate-400"}`} />
                  {isClockedIn ? "Shift in Progress" : todayRecord.punch_out_time ? "Shift Concluded Today" : "Ready for On-Site Check-In"}
                </span>

                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  {todayRecord.punch_in_location?.location_name || "Jaipur Campus (Flagship Academy)"}
                </span>
              </div>

              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Logged Shift Duration
                </p>
                <div className="text-3xl sm:text-4xl font-bold font-mono text-slate-900 tracking-tight mt-0.5">
                  {isClockedIn ? formatElapsed(elapsedSeconds) : todayRecord.punch_out_time ? (todayRecord.duration_formatted || "Completed") : "00h 00m 00s"}
                </div>
              </div>

              {/* Progress Bar (Standard 8.5h workday) */}
              <div className="space-y-1.5 max-w-md">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Daily Shift Goal (8.5h Standard)</span>
                  <span className="font-semibold text-slate-700">{isClockedIn ? `${shiftProgressPercent}%` : todayRecord.punch_out_time ? "100%" : "0%"}</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 rounded-full transition-all duration-300"
                    style={{ width: `${isClockedIn ? shiftProgressPercent : todayRecord.punch_out_time ? 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Timestamps */}
              <div className="flex items-center gap-4 text-xs text-slate-600 pt-1 flex-wrap font-mono">
                <span>Punch-In: <strong className="text-slate-900">{todayRecord.punch_in_time || "Pending"}</strong></span>
                <span>•</span>
                <span>Punch-Out: <strong className="text-slate-900">{todayRecord.punch_out_time || (isClockedIn ? "Active" : "Pending")}</strong></span>
              </div>
            </div>

            {/* Attendance Action CTAs */}
            <div className="flex flex-col sm:flex-row md:flex-col gap-2.5 shrink-0 justify-center">
              {!isClockedIn ? (
                <button
                  onClick={() => openPunchModal("in")}
                  className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm transition-all flex items-center justify-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  Punch In (Selfie & GPS)
                </button>
              ) : (
                <button
                  onClick={() => openPunchModal("out")}
                  className="px-6 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm shadow-sm transition-all flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Punch Out & Submit Report
                </button>
              )}

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsTaskModalOpen(true)}
                  className="flex-1 px-3 py-2 rounded-xl bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5 text-slate-500" />
                  + Assign Task
                </button>

                <button
                  onClick={() => {
                    setActiveTab("daily_report");
                    fetchDailyReports();
                  }}
                  className={`flex-1 px-3 py-2 rounded-xl border text-xs font-medium transition-colors flex items-center justify-center gap-1.5 shadow-sm ${
                    todayRecord?.eod_submitted
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
                      : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                  }`}
                >
                  <FileText className="w-3.5 h-3.5 text-slate-500" />
                  <span>{todayRecord?.eod_submitted ? "EOD: Submitted ✅" : "EOD Report ⏳"}</span>
                </button>
              </div>
            </div>
          </div>

          {/* ===================== TODAY'S VERIFICATION DETAILS ===================== */}
          <div className="pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Check In Verification */}
            <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/60 flex items-start gap-3.5">
              {todayRecord.punch_in_selfie ? (
                <img
                  src={todayRecord.punch_in_selfie}
                  alt="Clock in selfie"
                  onClick={() => setInspectedLog({ title: "Check-In Selfie Verification", image: todayRecord.punch_in_selfie, time: todayRecord.punch_in_time, loc: todayRecord.punch_in_location })}
                  className="w-14 h-14 rounded-lg object-cover ring-1 ring-emerald-300 shadow-sm cursor-pointer hover:opacity-90 transition-opacity shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                  <Camera className="w-5 h-5" />
                </div>
              )}
              <div className="min-w-0 space-y-0.5">
                <span className="text-[10px] font-semibold uppercase text-emerald-700 block">Check-In Verification</span>
                <p className="text-sm font-semibold text-slate-900 font-mono">
                  {todayRecord.punch_in_time || "Not Recorded"}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {todayRecord.punch_in_location?.location_name || "Campus Geofence Pending"}
                </p>
                {todayRecord.punch_in_location?.latitude && (
                  <a
                    href={`https://www.google.com/maps?q=${todayRecord.punch_in_location.latitude},${todayRecord.punch_in_location.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1 pt-0.5 font-medium"
                  >
                    View on Google Maps <ArrowUpRight className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>

            {/* Check Out Verification */}
            <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/60 flex items-start gap-3.5">
              {todayRecord.punch_out_selfie ? (
                <img
                  src={todayRecord.punch_out_selfie}
                  alt="Checkout selfie"
                  onClick={() => setInspectedLog({ title: "Check-Out Selfie Verification", image: todayRecord.punch_out_selfie, time: todayRecord.punch_out_time, loc: todayRecord.punch_out_location })}
                  className="w-14 h-14 rounded-lg object-cover ring-1 ring-blue-300 shadow-sm cursor-pointer hover:opacity-90 transition-opacity shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                  <Camera className="w-5 h-5" />
                </div>
              )}
              <div className="min-w-0 space-y-0.5">
                <span className="text-[10px] font-semibold uppercase text-slate-600 block">Check-Out Verification</span>
                <p className="text-sm font-semibold text-slate-900 font-mono">
                  {todayRecord.punch_out_time || (isClockedIn ? "Shift in progress..." : "No checkout yet")}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {todayRecord.punch_out_location?.location_name || (isClockedIn ? "Will lock upon checkout" : "Jaipur Floor")}
                </p>
                {todayRecord.punch_out_location?.latitude && (
                  <a
                    href={`https://www.google.com/maps?q=${todayRecord.punch_out_location.latitude},${todayRecord.punch_out_location.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1 pt-0.5 font-medium"
                  >
                    View on Google Maps <ArrowUpRight className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================================
            SECTION 2: REFINED NAVIGATION TABS (SEGMENTED CONTROL STYLE)
            ========================================================================= */}
        <div className="border-b border-slate-200 flex items-center gap-1 overflow-x-auto pb-px">
          {[
            { id: "overview", label: "Overview & Focus", icon: LayoutDashboard },
            { id: "tasks", label: `Tasks (${tasks.length})`, icon: ClipboardList },
            { id: "daily_report", label: "Daily EOD Report", icon: FileText },
            { id: "downloads", label: "Download Reports", icon: Download },
            { id: "history", label: "Attendance History", icon: CalendarDays },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === "daily_report") fetchDailyReports();
                }}
                className={`py-2.5 px-3.5 text-xs sm:text-sm font-medium border-b-2 transition-all flex items-center gap-2 whitespace-nowrap ${
                  isActive
                    ? "border-emerald-600 text-emerald-700 font-semibold"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* =========================================================================
            TAB 1: OVERVIEW & ACTIVE FOCUS
            ========================================================================= */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            
            {/* Today's Focus Tasks Widget */}
            <div className="p-5 sm:p-6 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 flex-wrap gap-2">
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                    <CheckSquare className="w-4 h-4 text-emerald-600" />
                    Today's Active Tasks ({todayDoneTasks}/{tasks.length} Completed)
                  </h3>
                  <p className="text-xs text-slate-500">
                    Track your daily targets and check off items as you complete them
                  </p>
                </div>
                <button
                  onClick={() => setIsTaskModalOpen(true)}
                  className="px-3 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-medium transition-colors flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5 text-slate-500" /> + Add Task
                </button>
              </div>

              {tasks.length === 0 ? (
                <div className="p-8 text-center bg-slate-50/50 rounded-xl border border-slate-200/60">
                  <CheckSquare className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-slate-700">No active tasks created yet</p>
                  <p className="text-xs text-slate-500 mt-0.5">Click "+ Add Task" to set a goal or deliverable for today.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {tasks.slice(0, 4).map((t) => {
                    const isDone = t.status === "completed";
                    const checklist = t.checklist || [];

                    return (
                      <div
                        key={t.task_id}
                        className={`p-4 rounded-xl border transition-all ${
                          isDone
                            ? "bg-slate-50/60 border-slate-200/60 opacity-80"
                            : "bg-white border-slate-200 shadow-sm"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-1.5">
                          <div className="min-w-0">
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                              {t.category || "Operations"}
                            </span>
                            <h4 className={`text-sm font-semibold text-slate-900 mt-1 truncate ${isDone ? "line-through text-slate-400" : ""}`}>
                              {t.title}
                            </h4>
                          </div>

                          <button
                            onClick={() => handleToggleTaskStatus(t.task_id, t.status)}
                            className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all shrink-0 ${
                              isDone
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                            }`}
                          >
                            {isDone ? "Completed" : "In Progress"}
                          </button>
                        </div>

                        {checklist.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                            {checklist.slice(0, 3).map((item, idx) => (
                              <div
                                key={item.id || idx}
                                onClick={() => handleToggleChecklistItem(t, idx)}
                                className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 cursor-pointer select-none py-0.5"
                              >
                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                                  item.done ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300"
                                }`}>
                                  {item.done && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                                </div>
                                <span className={`text-xs truncate ${item.done ? "line-through text-slate-400" : ""}`}>
                                  {item.text}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Official Mandates & Operational KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm space-y-1.5">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Flame className="w-4 h-4 text-emerald-600" /> Daily Must-Do Mandate
                </span>
                <p className="text-xs text-slate-700 leading-relaxed font-medium">
                  {currentUser.daily_must_do || "Morning trading standup + active task tracking + transparent EOD report submission."}
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm space-y-1.5">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Target className="w-4 h-4 text-slate-700" /> Core KPIs & Deliverables
                </span>
                <p className="text-xs text-slate-700 leading-relaxed font-medium">
                  {currentUser.core_kpis || "High availability, zero-defect execution, discipline, strict SLA adherence."}
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm space-y-1.5">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Award className="w-4 h-4 text-amber-600" /> Escalation Hierarchy
                </span>
                <p className="text-xs text-slate-700 leading-relaxed font-medium">
                  Direct Escalation Route: <strong className="text-slate-900">{currentUser.escalate_to || "Deepak Sethia (Founder)"}</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================================
            TAB 2: TASKS & DELIVERABLES
            ========================================================================= */}
        {activeTab === "tasks" && (
          <div className="space-y-4">
            
            {/* Header with filters and create button */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <div>
                <h3 className="font-semibold text-slate-900 text-base">Deliverables & Task Management</h3>
                <p className="text-xs text-slate-500">Self-assigned and manager-assigned operational tasks</p>
              </div>

              <button
                onClick={() => setIsTaskModalOpen(true)}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs shadow-sm transition-all flex items-center gap-1.5 self-start sm:self-auto"
              >
                <Plus className="w-3.5 h-3.5" />
                + Assign Task to Myself
              </button>
            </div>

            {/* Task Filters */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
              {[
                { id: "all", label: `All Tasks (${tasks.length})` },
                { id: "in_progress", label: "In Progress" },
                { id: "completed", label: "Completed" },
                { id: "admin", label: "Admin Assigned" },
                { id: "self", label: "Self Assigned" }
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setTaskFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg font-medium shrink-0 transition-all ${
                    taskFilter === f.id
                      ? "bg-slate-900 text-white shadow-sm"
                      : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Task Cards */}
            {filteredTasks.length === 0 ? (
              <div className="py-16 text-center bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <h4 className="text-slate-800 font-semibold text-sm">No tasks in this category</h4>
                <p className="text-xs text-slate-500 mt-0.5">Click the button below to add an operational deliverable.</p>
                <button
                  onClick={() => setIsTaskModalOpen(true)}
                  className="mt-3 px-3.5 py-1.5 rounded-lg bg-emerald-600 text-white font-medium text-xs shadow-sm"
                >
                  + Create Task
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTasks.map((t) => {
                  const isDone = t.status === "completed";
                  const checklist = t.checklist || [];
                  const doneCount = checklist.filter(c => c.done).length;

                  return (
                    <div
                      key={t.task_id}
                      className={`p-4 rounded-xl border transition-all ${
                        isDone
                          ? "bg-slate-50/70 border-slate-200/70 opacity-80"
                          : "bg-white border-slate-200 shadow-sm"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className={`font-semibold text-sm text-slate-900 ${isDone ? "line-through text-slate-400" : ""}`}>
                              {t.title}
                            </h4>
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                              {t.category || "Operations"}
                            </span>
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${
                              t.priority?.includes("P0") ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"
                            }`}>
                              {t.priority || "Medium"}
                            </span>
                            {t.is_admin_assigned ? (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                                Assigned by Manager
                              </span>
                            ) : (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                                Self-Assigned
                              </span>
                            )}
                          </div>
                          {t.description && (
                            <p className="text-xs text-slate-600 leading-relaxed">
                              {t.description}
                            </p>
                          )}
                        </div>

                        {/* Status Toggle Button */}
                        <button
                          onClick={() => handleToggleTaskStatus(t.task_id, t.status)}
                          className={`px-3 py-1.5 rounded-lg font-medium text-xs shrink-0 flex items-center gap-1.5 transition-all ${
                            isDone
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : t.status === "in_progress"
                              ? "bg-amber-50 text-amber-700 border border-amber-200"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {isDone ? (
                            <>
                              <CheckCircle className="w-3.5 h-3.5" /> Completed
                            </>
                          ) : t.status === "in_progress" ? (
                            <>
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" /> In Progress
                            </>
                          ) : (
                            <>
                              <Clock className="w-3.5 h-3.5" /> To Do
                            </>
                          )}
                        </button>
                      </div>

                      {/* Interactive Checklist */}
                      {checklist.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                            <span>Checklist ({doneCount}/{checklist.length})</span>
                            <span className="font-semibold text-emerald-600">{Math.round((doneCount / checklist.length) * 100)}%</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {checklist.map((item, idx) => (
                              <div
                                key={item.id || idx}
                                onClick={() => handleToggleChecklistItem(t, idx)}
                                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50 transition-colors text-xs cursor-pointer select-none"
                              >
                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                                  item.done ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300"
                                }`}>
                                  {item.done && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                                </div>
                                <span className={`text-xs text-slate-700 ${item.done ? "line-through text-slate-400" : ""}`}>
                                  {item.text}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* =========================================================================
            TAB 3: DAILY WORK REPORT (EOD REPORT WRITER)
            ========================================================================= */}
        {activeTab === "daily_report" && (
          <div className="space-y-6">
            <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 flex-wrap gap-2">
                <div>
                  <h3 className="font-semibold text-slate-900 text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    Submit End-of-Day (EOD) Work Report
                  </h3>
                  <p className="text-xs text-slate-500">
                    Transparent daily report of deliverables, accomplishments, and blockers
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSuggestCompletedTasks}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-medium transition-all flex items-center gap-1"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                    Import Today's Tasks
                  </button>
                  <span className="text-xs font-mono font-medium text-slate-600 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
                    {dateStr}
                  </span>
                </div>
              </div>

              {todayRecord?.eod_submitted && (
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-emerald-900 flex items-center gap-2">
                      <span>Today's EOD Report Successfully Logged</span>
                      <span className="text-[10px] font-normal font-mono bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                        {todayRecord.eod_submitted_at ? `Submitted at ${new Date(todayRecord.eod_submitted_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "Recorded"}
                      </span>
                    </h4>
                    <p className="text-[11px] text-emerald-700 mt-0.5 leading-relaxed">
                      Your daily work summary is securely logged with management. You can review your submission below, or update and submit new details anytime before leaving campus.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmitDailyReport} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    What did you accomplish today? * (Deliverables, fixes, student doubts, calls, trading setups...)
                  </label>
                  <textarea
                    rows={4}
                    required
                    placeholder="Describe your day's work in detail: tasks completed, code deployed, student queries answered, calls completed..."
                    value={dailyReportSummary}
                    onChange={(e) => setDailyReportSummary(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg p-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 leading-relaxed"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Any Blockers or Tomorrow's Key Priorities (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Waiting on API credentials from partner; will finish onboarding at 9:30 AM tomorrow."
                    value={dailyReportBlockers}
                    onChange={(e) => setDailyReportBlockers(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
                  />
                </div>

                <div className="flex justify-end pt-1">
                  <button
                    type="submit"
                    disabled={submittingDailyReport}
                    className="px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs shadow-sm transition-all flex items-center gap-2"
                  >
                    {submittingDailyReport ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    Submit Daily Report
                  </button>
                </div>
              </form>
            </div>

            {/* Submitted Daily Reports Archive */}
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Previously Submitted Reports
              </h4>

              {loadingDailyReports ? (
                <div className="py-8 text-center text-slate-400 text-xs">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto text-emerald-600 mb-2" />
                  Loading past reports...
                </div>
              ) : dailyReportsList.length === 0 ? (
                <div className="p-8 text-center bg-white rounded-xl border border-slate-200 text-xs text-slate-500 shadow-sm">
                  No daily reports submitted yet. Write and submit your first report above.
                </div>
              ) : (
                <div className="space-y-3">
                  {dailyReportsList.map((rep) => (
                    <div
                      key={rep.report_id || rep.date}
                      className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm space-y-2"
                    >
                      <div className="flex items-center justify-between text-xs border-b border-slate-100 pb-2">
                        <span className="font-semibold text-emerald-700 font-mono">
                          {rep.date}
                        </span>
                        {rep.hours_worked && (
                          <span className="text-slate-500 font-mono text-xs">
                            Hours: <strong className="text-slate-800">{rep.hours_worked}h</strong>
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {rep.summary}
                      </p>
                      {rep.blockers && (
                        <p className="text-[11px] text-amber-700 pt-1.5 border-t border-slate-100 italic">
                          Blockers / Notes: {rep.blockers}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* =========================================================================
            TAB 4: DOWNLOAD REPORTS (15 DAYS & MONTHLY AUDIT)
            ========================================================================= */}
        {activeTab === "downloads" && (
          <div className="space-y-5">
            <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
              <div>
                <h3 className="font-semibold text-slate-900 text-base flex items-center gap-2">
                  <Download className="w-4 h-4 text-emerald-600" />
                  Official Attendance & Performance Reports
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Export verifiable records of check-ins, check-outs, selfie verifications, shift hours, and daily reports.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                <div className="p-5 rounded-xl bg-slate-50 border border-slate-200 space-y-3 flex flex-col justify-between">
                  <div>
                    <span className="text-[10px] font-medium uppercase text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      Fortnightly
                    </span>
                    <h4 className="text-base font-semibold text-slate-900 mt-1.5">
                      15 Days Performance Dossier
                    </h4>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Includes past 15 calendar days of attendance, hours worked, geofences, and daily work summaries.
                    </p>
                  </div>

                  <button
                    onClick={() => handleOpenReportDownload(15)}
                    disabled={loadingDownload}
                    className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs shadow-sm transition-all flex items-center justify-center gap-1.5"
                  >
                    {loadingDownload ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    Download 15-Day Report
                  </button>
                </div>

                <div className="p-5 rounded-xl bg-slate-50 border border-slate-200 space-y-3 flex flex-col justify-between">
                  <div>
                    <span className="text-[10px] font-medium uppercase text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                      Full Month
                    </span>
                    <h4 className="text-base font-semibold text-slate-900 mt-1.5">
                      Monthly (30 Days) Audit Report
                    </h4>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                      Complete monthly dossier with working day compliance, hours worked, and monthly KPI delivery.
                    </p>
                  </div>

                  <button
                    onClick={() => handleOpenReportDownload(30)}
                    disabled={loadingDownload}
                    className="w-full py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs shadow-sm transition-all flex items-center justify-center gap-1.5"
                  >
                    {loadingDownload ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    Download Monthly Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================================
            TAB 5: CONTINUOUS ATTENDANCE HISTORY
            ========================================================================= */}
        {activeTab === "history" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <div>
                <h3 className="font-semibold text-slate-900 text-base">Continuous Attendance Ledger</h3>
                <p className="text-xs text-slate-500">Historical record of check-in times, check-out times, and locations</p>
              </div>

              {/* Filters */}
              <div className="flex items-center gap-1.5 text-xs">
                {["all", "present", "off"].map(f => (
                  <button
                    key={f}
                    onClick={() => setSelectedDateFilter(f)}
                    className={`px-3 py-1.5 rounded-lg font-medium capitalize ${
                      selectedDateFilter === f
                        ? "bg-slate-900 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200">
                  <tr>
                    <th className="p-3.5">Date</th>
                    <th className="p-3.5">Selfie</th>
                    <th className="p-3.5">Punch-In</th>
                    <th className="p-3.5">Punch-Out</th>
                    <th className="p-3.5">Duration</th>
                    <th className="p-3.5">Status</th>
                    <th className="p-3.5">Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRecords.map((r, i) => (
                    <tr key={r.date || i} className="hover:bg-slate-50/60 transition-colors">
                      <td className="p-3.5 font-medium text-slate-900 font-mono">
                        {r.display_date} ({r.day_name})
                      </td>
                      <td className="p-3.5">
                        {r.punch_in_selfie ? (
                          <img
                            src={r.punch_in_selfie}
                            alt="Log selfie"
                            onClick={() => setInspectedLog({ title: `${r.display_date} Punch Selfie`, image: r.punch_in_selfie, time: r.punch_in_time, loc: { location_name: r.location_name } })}
                            className="w-7 h-7 rounded-md object-cover ring-1 ring-slate-200 cursor-pointer hover:opacity-80"
                          />
                        ) : (
                          <span className="text-slate-400 font-mono">--</span>
                        )}
                      </td>
                      <td className="p-3.5 font-semibold text-emerald-700 font-mono">
                        {r.punch_in_time}
                      </td>
                      <td className="p-3.5 font-mono text-slate-600">
                        {r.punch_out_time}
                      </td>
                      <td className="p-3.5 font-mono text-slate-800">
                        {r.duration_formatted}
                      </td>
                      <td className="p-3.5">
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${
                          r.status === "present" || r.status === "clocked_in"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : r.status === "weekend_off"
                            ? "bg-slate-100 text-slate-600 border border-slate-200"
                            : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="p-3.5 text-slate-500 text-[11px] truncate max-w-[150px]">
                        {r.location_name || "Jaipur Campus"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card Layout (No horizontal overflow!) */}
            <div className="md:hidden space-y-2.5">
              {filteredRecords.map((r, i) => (
                <div key={r.date || i} className="p-3.5 rounded-xl bg-white border border-slate-200 shadow-sm space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-900 font-mono">{r.display_date} ({r.day_name})</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${
                      r.status === "present" || r.status === "clocked_in"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : r.status === "weekend_off"
                        ? "bg-slate-100 text-slate-600"
                        : "bg-amber-50 text-amber-700"
                    }`}>
                      {r.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-mono text-slate-600">
                    <div>
                      In: <strong className="text-emerald-700">{r.punch_in_time}</strong> • Out: <strong>{r.punch_out_time}</strong>
                    </div>
                    <div>
                      Duration: <strong>{r.duration_formatted}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* =========================================================================
          MODAL: CAMERA SCANNER (PUNCH IN / PUNCH OUT)
          ========================================================================= */}
      <AnimatePresence>
        {isPunchModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="w-full max-w-md bg-white rounded-2xl p-5 sm:p-6 border border-slate-200 shadow-2xl relative"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900 text-base flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    {punchMode === "in" ? "Biometric Check-In" : "Check-Out Verification"}
                  </h3>
                  <p className="text-xs text-slate-500">Live facial & geolocation authentication</p>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={toggleCameraFacingMode}
                    title="Switch camera"
                    className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
                  >
                    <SwitchCamera className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setIsPunchModalOpen(false);
                      stopCamera();
                    }}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Camera Window */}
              <div className="relative aspect-square max-h-[300px] w-full rounded-xl bg-slate-900 overflow-hidden flex items-center justify-center mx-auto">
                {!capturedImage ? (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover mirror"
                    />
                    <canvas ref={canvasRef} className="hidden" />

                    <div className="absolute inset-0 pointer-events-none flex flex-col justify-between p-3">
                      <div className="flex justify-between items-center text-[10px] font-mono text-white bg-slate-900/70 px-2 py-0.5 rounded backdrop-blur self-start">
                        <span>LIVE VERIFICATION</span>
                      </div>
                      
                      <div className="self-center w-32 h-32 border-2 border-dashed border-emerald-400/70 rounded-full" />

                      <div className="text-center text-[10px] font-mono text-white bg-slate-900/70 px-2 py-0.5 rounded backdrop-blur self-center">
                        Position face inside circle
                      </div>
                    </div>
                  </>
                ) : (
                  <img
                    src={capturedImage}
                    alt="Captured selfie"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Location Pill */}
              <div className="mt-3 p-2.5 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${locationStatus === "locked" ? "bg-emerald-600" : "bg-amber-500 animate-pulse"}`} />
                  <span className="font-mono text-slate-700 text-[11px]">
                    {locationStatus === "locked"
                      ? `GPS: ${gpsCoords?.latitude?.toFixed(4)}, ${gpsCoords?.longitude?.toFixed(4)}`
                      : "Locating geofence coordinates..."}
                  </span>
                </div>
                <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  Jaipur Campus
                </span>
              </div>

              {/* EOD Report input for Punch Out */}
              {punchMode === "out" && (
                <div className="mt-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-medium text-slate-700">
                      What did you accomplish today? (EOD Summary) *
                    </label>
                    <button
                      type="button"
                      onClick={handleSuggestCompletedTasksForPunch}
                      className="text-[11px] font-medium text-emerald-600 hover:text-emerald-700 hover:underline flex items-center gap-1"
                    >
                      ✨ Auto-fill Tasks
                    </button>
                  </div>
                  <textarea
                    rows={2}
                    placeholder="Briefly summarize your day's work before checking out..."
                    value={eodPunchReport}
                    onChange={(e) => setEodPunchReport(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
                  />
                </div>
              )}

              {/* Buttons */}
              <div className="mt-4 flex items-center gap-2">
                {!capturedImage ? (
                  <button
                    onClick={captureSelfie}
                    className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm shadow-sm transition-all flex items-center justify-center gap-2"
                  >
                    <Camera className="w-4 h-4" />
                    Capture Photo
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setCapturedImage(null);
                        startCamera();
                      }}
                      className="py-2.5 px-3.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium transition-colors"
                    >
                      Retake
                    </button>
                    <button
                      onClick={handleSubmitPunch}
                      disabled={submittingPunch}
                      className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm shadow-sm transition-all flex items-center justify-center gap-1.5"
                    >
                      {submittingPunch ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Confirm & {punchMode === "in" ? "Clock In" : "Clock Out"}
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* =========================================================================
          MODAL: SELF-TASK ASSIGNMENT
          ========================================================================= */}
      <AnimatePresence>
        {isTaskModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="w-full max-w-lg bg-white rounded-2xl p-6 border border-slate-200 shadow-2xl relative"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900 text-base">
                    {data?.is_founder ? "Assign Operational Deliverable" : "Assign Task to Station"}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {data?.is_founder ? "Assign a priority deliverable to yourself or any team member" : "Create an operational deliverable for your station"}
                  </p>
                </div>
                <button
                  onClick={() => setIsTaskModalOpen(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleCreateSelfTask} className="space-y-3.5">
                {(data?.is_founder || data?.user?.role === "admin" || staffRoster.length > 0) && (
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Assign Deliverable To</label>
                    <select
                      value={newTaskAssigneeEmail}
                      onChange={(e) => setNewTaskAssigneeEmail(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 font-medium"
                    >
                      <option value="">Myself ({currentUser.name})</option>
                      {staffRoster.map((m) => (
                        <option key={m.email} value={m.email}>
                          {m.name} — {m.department || "Operations"} ({m.team_title || "Staff"})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Task Title *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Complete trading floor review, audit student doubts"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Description (Optional)</label>
                  <textarea
                    rows={2}
                    placeholder="Details, execution steps, expected outcome..."
                    value={newTaskDesc}
                    onChange={(e) => setNewTaskDesc(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
                    <select
                      value={newTaskCategory}
                      onChange={(e) => setNewTaskCategory(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
                    >
                      <option value="Operations">Operations</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Sales & Admissions">Sales & Admissions</option>
                      <option value="Trading Support">Trading Support</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Recruitment">Recruitment</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Priority</label>
                    <select
                      value={newTaskPriority}
                      onChange={(e) => setNewTaskPriority(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
                    >
                      <option value="P0 (Urgent)">P0 (Urgent)</option>
                      <option value="P1 (High)">P1 (High)</option>
                      <option value="P2 (Normal)">P2 (Normal)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Checklist Items (One per line)</label>
                  <textarea
                    rows={3}
                    placeholder="1) Pull latest Git branch&#10;2) Verify unit test coverage&#10;3) Confirm Render deploy"
                    value={newTaskChecklist}
                    onChange={(e) => setNewTaskChecklist(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-900 font-mono placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setIsTaskModalOpen(false)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:text-slate-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingTask}
                    className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs shadow-sm"
                  >
                    {submittingTask ? "Saving..." : "Save Task"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* =========================================================================
          MODAL: REPORT PREVIEW & EXPORT (15-DAY / MONTHLY)
          ========================================================================= */}
      <AnimatePresence>
        {downloadModalOpen && downloadReportData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 sm:p-5 border-b border-slate-200 flex items-center justify-between gap-3 bg-slate-50/70">
                <div>
                  <h3 className="font-semibold text-slate-900 text-base sm:text-lg">
                    {downloadReportData.period_label}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Staff Member: <strong className="text-slate-800">{downloadReportData.member?.name}</strong> ({downloadReportData.member?.team_title})
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={exportReportToCSV}
                    className="px-3 py-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs font-medium flex items-center gap-1.5 shadow-sm"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5" /> CSV Export
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs flex items-center gap-1.5 shadow-sm"
                  >
                    <Printer className="w-3.5 h-3.5" /> Print / PDF
                  </button>
                  <button
                    onClick={() => setDownloadModalOpen(false)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Printable Body */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 text-xs">
                {/* Metrics Summary Strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <div>
                    <span className="text-xs text-slate-500 font-mono">Days Present</span>
                    <p className="text-xl font-bold font-mono text-emerald-700 mt-0.5">
                      {downloadReportData.stats?.days_present} / {downloadReportData.period_days}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 font-mono">Total Hours</span>
                    <p className="text-xl font-bold font-mono text-slate-900 mt-0.5">
                      {downloadReportData.stats?.total_hours}h
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 font-mono">Compliance Rate</span>
                    <p className="text-xl font-bold font-mono text-emerald-700 mt-0.5">
                      {downloadReportData.stats?.attendance_rate}%
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 font-mono">Tasks Completed</span>
                    <p className="text-xl font-bold font-mono text-slate-900 mt-0.5">
                      {downloadReportData.stats?.completed_tasks} / {downloadReportData.stats?.total_tasks}
                    </p>
                  </div>
                </div>

                {/* Table of Records */}
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 text-slate-600 font-mono text-[11px] border-b border-slate-200">
                      <tr>
                        <th className="p-3">Date</th>
                        <th className="p-3">In (IST)</th>
                        <th className="p-3">Out (IST)</th>
                        <th className="p-3">Duration</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Location</th>
                        <th className="p-3">Daily Work Report</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {downloadReportData.records?.map((r, i) => (
                        <tr key={r.date || i} className="hover:bg-slate-50">
                          <td className="p-3 font-mono font-medium text-slate-900 whitespace-nowrap">
                            {r.display_date} ({r.day})
                          </td>
                          <td className="p-3 font-mono text-emerald-700 font-semibold">
                            {r.punch_in_time}
                          </td>
                          <td className="p-3 font-mono text-slate-600">
                            {r.punch_out_time}
                          </td>
                          <td className="p-3 font-mono text-slate-800 font-medium">
                            {r.duration}
                          </td>
                          <td className="p-3">
                            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                              r.status === "present" || r.status === "clocked_in"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : r.status === "weekly_off"
                                ? "bg-slate-100 text-slate-600"
                                : "bg-amber-50 text-amber-700"
                            }`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="p-3 text-slate-500 text-[11px] truncate max-w-[140px]">
                            {r.location?.split("(")[0]?.trim() || "Jaipur Campus"}
                          </td>
                          <td className="p-3 text-slate-700 max-w-xs">
                            {r.eod_report ? (
                              <span className="line-clamp-2 text-slate-700">{r.eod_report}</span>
                            ) : (
                              <span className="text-slate-400">--</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* =========================================================================
          MODAL: INSPECT SELFIE / RECORD
          ========================================================================= */}
      <AnimatePresence>
        {inspectedLog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="w-full max-w-sm bg-white rounded-2xl p-5 border border-slate-200 shadow-2xl space-y-3"
            >
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <h4 className="font-semibold text-slate-900 text-sm">{inspectedLog.title}</h4>
                <button
                  onClick={() => setInspectedLog(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <img
                src={inspectedLog.image}
                alt="Enlarged selfie"
                className="w-full h-auto max-h-[320px] object-cover rounded-xl border border-slate-200 shadow-sm"
              />

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono space-y-1">
                {inspectedLog.time && (
                  <p className="text-slate-700">
                    Timestamp: <strong className="text-emerald-700">{inspectedLog.time} IST</strong>
                  </p>
                )}
                {inspectedLog.loc?.location_name && (
                  <p className="text-slate-500 truncate">
                    Campus: {inspectedLog.loc.location_name}
                  </p>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* ===================== PROFILE PICTURE SELF-SERVICE MODAL ===================== */}
        {showPhotoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-sm rounded-3xl bg-white border border-slate-200 shadow-2xl p-6 text-slate-900"
            >
              <div className="flex items-center justify-between border-b pb-3 mb-5 border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
                    <Camera className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Profile Picture</h3>
                    <p className="text-xs text-slate-500">{currentUser?.name}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPhotoModal(false)}
                  className="rounded-full p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Avatar Preview */}
              <div className="flex flex-col items-center justify-center space-y-4 my-2">
                <div className="relative">
                  <UserAvatar
                    src={currentUser?.picture}
                    alt={currentUser?.name}
                    className="w-28 h-28 rounded-full border-4 border-white shadow-lg ring-2 ring-slate-100"
                  />
                </div>

                <p className="text-xs text-slate-500 text-center max-w-xs">
                  {currentUser?.picture
                    ? "Your custom profile picture is active across JamaDhan OS."
                    : "You are currently using the default blank DP. Upload your own photo anytime."}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 mt-6 pt-4 border-t border-slate-100">
                <label className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs transition-colors cursor-pointer shadow-sm ${uploadingPhoto ? "opacity-70 pointer-events-none" : ""}`}>
                  <Camera className="w-4 h-4" />
                  <span>{uploadingPhoto ? "Uploading..." : (currentUser?.picture ? "Upload New Photo" : "Upload Profile Picture")}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingPhoto}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleUploadMyPhoto(e.target.files[0]);
                      }
                    }}
                  />
                </label>

                {currentUser?.picture && (
                  <button
                    type="button"
                    onClick={handleRemoveMyPhoto}
                    className="w-full flex items-center justify-center gap-1.5 py-2 px-4 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Remove Photo (Use Blank DP)</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setShowPhotoModal(false)}
                  className="w-full py-2 text-xs font-medium text-slate-500 hover:text-slate-800"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
